import axios from "axios";
import router from "@/router";
import { reportService } from "../request";

// 上传专用实例：不挂 request.js 的全局拦截器（原因见 uploadKnowledgeDoc）
export const uploadService = axios.create({ baseURL: "/api/report-system" });

// 会话失效（HTTP 401 或 errcode===1）时与 request.js 一致：清本地登录态、跳登录页
function sessionExpired() {
  router.push("/login");
  localStorage.removeItem("userInfo");
  return Promise.reject(new Error("登录状态已失效"));
}

/**
 * 获取中心知识库文档列表（关键词全文搜索 + 分页）
 * @param {Object} params 查询参数 {keyword, page, page_size}
 * @returns {Promise} 返回Promise对象
 */
export const listKnowledgeDocs = (params = {}) => {
  return reportService.get("/knowledge-base/docs", { params });
};

/**
 * 上传单个文档（页面逐个文件顺序调用），大文件不设超时。
 * 不走 reportService：其响应拦截器对 HTTP 错误（如 nginx 的 413/504）返回永不 settle 的 Promise，
 * 上传状态会一直转圈。这里用不带拦截器的独立实例，任何失败都 reject，由调用方逐个文件处理；
 * 会话失效（401 / errcode=1）照 request.js 清 userInfo 并跳 /login。
 * @param {File} file 文件，以 multipart 字段 files 提交
 * @param {Function} onUploadProgress axios 进度回调
 * @returns {Promise} resolve 为后端响应体 {errcode, errmsg, data: [{name, ok, error, id}]}
 */
export const uploadKnowledgeDoc = (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append("files", file);
  return uploadService
    .post("/knowledge-base/docs", formData, { timeout: 0, onUploadProgress })
    .then(
      (response) => {
        const data = response.data;
        if (data && data.errcode === 1) return sessionExpired();
        return data;
      },
      (error) => {
        if (error && error.response && error.response.status === 401) return sessionExpired();
        return Promise.reject(error);
      }
    );
};

/**
 * 重命名文档（不含扩展名）
 * @param {Number} id 文档ID
 * @param {String} name 新名称
 * @returns {Promise} 返回Promise对象
 */
export const renameKnowledgeDoc = (id, name) => {
  return reportService.put(`/knowledge-base/docs/${id}`, { name });
};

/**
 * 删除（软删）文档
 * @param {Number} id 文档ID
 * @returns {Promise} 返回Promise对象
 */
export const deleteKnowledgeDoc = (id) => {
  return reportService.delete(`/knowledge-base/docs/${id}`);
};

/**
 * 文档原文件的下载地址（交给浏览器原生下载，不经 axios）
 * @param {Number} id 文档ID
 * @returns {String} 下载接口地址
 */
export const knowledgeDocDownloadUrl = (id) => {
  return `/api/report-system/knowledge-base/docs/${id}/download`;
};

/**
 * 取预览方式与定位结果：后端按文件类型返回判别联合（kind = pdf/text/image/audio/download_only）。
 * 前端因此不持有「扩展名 → 预览模式」的映射表，以后加格式是纯后端改动。
 * @param {Number} id 文档ID
 * @param {Object} params {offset, keyword}，由 previewQuery 生成
 * @returns {Promise} 返回Promise对象
 */
export const knowledgeDocPreviewInfo = (id, params = {}) => {
  return reportService.get(`/knowledge-base/docs/${id}/preview-info`, { params });
};

/**
 * 预览用的 inline 二进制流地址（交给 iframe / img / audio 直接取，同源自动带登录 cookie）
 * @param {Number} id 文档ID
 * @returns {String} 预览接口地址
 */
export const knowledgeDocPreviewUrl = (id) => {
  return `/api/report-system/knowledge-base/docs/${id}/preview`;
};

/**
 * PDF 预览的 iframe src：自带的 pdf.js 阅读器（web/public/pdfjs-4.10.38，见那里的 README）。
 * 不用浏览器自带阅读器，是因为它会把整份文件从头拉完才显示（194MB 的默克手册要等几十秒），
 * pdf.js 经 Range 只拉看到的页；检索词高亮由 KnowledgeDocPreview 在每页文字层渲染时做。
 * #page 由阅读器解析；无页码时不拼空 fragment——那会让 src 每次都变、iframe 白白重载。
 * @param {Number} id 文档ID
 * @param {Number} page 1 基页码，缺省/0 表示不跳页
 * @returns {String} iframe 的 src
 */
export const pdfPreviewSrc = (id, page) => {
  const viewer = `/pdfjs-4.10.38/web/viewer.html?file=${encodeURIComponent(knowledgeDocPreviewUrl(id))}`;
  return page ? `${viewer}#page=${page}` : viewer;
};
